// Cancelamento de consulta (VF-3, correção 3) — puro.
//
// ★ POR QUE CANCELAR TEM PASSO DE CONFIRMAÇÃO, SE O FLAG NÃO TEM.
//
// A regra do flag — escrita a um toque, sem diálogo — vale porque confirmar é
// inofensivo e SIMÉTRICO: não move o funil, não cria tarefa, não entra em
// dinheiro, e o mesmo alvo desfaz. Cancelar não é nenhuma das duas coisas:
//
//   1. LIBERA A VAGA PARA TERCEIROS. No instante seguinte a recepção pode marcar
//      outra pessoa naquele horário; o efeito sai do documento e vira um fato da
//      clínica.
//   2. NÃO É SIMÉTRICO. Não existe "descancelar": voltar exige remarcar, e a
//      vaga original pode já ter sido tomada. Um toque errado no flag custa um
//      segundo toque; um cancelamento errado custa uma ligação ao paciente.
//   3. ESCREVE NA TIMELINE do lead, que é append-only — o registro fica.
//
// O diálogo não contradiz a regra do flag: a regra sempre foi sobre escrita
// inofensiva e reversível pelo mesmo gesto.

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Consulta que ainda vai acontecer e não foi cancelada. */
const ehFuturaViva = (a, agora) => {
  const quando = paraData(a?.dataHora);
  const status = a?.status ?? 'agendado';
  return Boolean(quando) && quando > agora && status !== 'cancelado' && status !== 'faltou';
};

/**
 * Sobra alguma consulta futura depois de cancelar esta?
 *
 * ★ É o que decide se o diálogo PERGUNTA sobre a etapa do lead. Se sobra, o lead
 * está agendado de verdade e não há o que perguntar. Se não sobra, manter a
 * etapa `agendado` produziria um lead agendado SEM consulta nenhuma — a mentira
 * no funil que o projeto já carrega como pendência. A pergunta só aparece no
 * caso em que a resposta importa.
 */
export function temOutraConsultaFutura(agendamentos = [], idCancelado, agora = new Date()) {
  return (agendamentos ?? []).some((a) => a?.id !== idCancelado && ehFuturaViva(a, agora));
}

/**
 * A etapa quando esta era a última consulta: "Não agendou" (VG).
 *
 * ⚠ Ela EXIGE motivo da lista — o diálogo mostra o select, com padrão
 * "Vai entrar em contato". Um motivo fixo inventado pelo sistema contaminaria
 * a lista que o gestor lê.
 */
/**
 * ★ VQ-3 — cancelar a ÚLTIMA consulta futura move o lead para NÃO COMPARECEU
 * (decisão do dono, 18/set/2026, contra a recomendação do plano e consciente
 * do custo). Condições que preservam o número: o agendamento fica `cancelado`
 * (NUNCA `faltou` — a taxa de comparecimento é compareceu ÷ marcados, em
 * agendamentos, e não muda por um cancelamento); a data de retorno é
 * obrigatória (vira tarefa no mesmo lote, como a VJ); "Não compareceu" não tem
 * motivo (VG). Se havia OUTRA consulta futura, nada de etapa muda.
 */
export const ETAPA_AO_CANCELAR = 'nao_compareceu';

/** Devolve a mensagem de erro, ou null. Voltar de etapa exige a data de retorno. */
export function validarCancelamento({ voltarEtapa = false, retornoEm = null } = {}) {
  if (!voltarEtapa) return null;
  const d = paraData(retornoEm);
  if (!d) return 'Informe a data do retorno.';
  return null;
}

/** O texto que vai para a timeline do lead. */
export function textoCancelamento(agendamento, motivo, formatar) {
  const quando = formatar(paraData(agendamento?.dataHora) ?? new Date());
  const limpo = String(motivo ?? '').trim();
  // "Consulta cancelada — …": a timeline diz cancelamento, nunca falta.
  return `Consulta cancelada — ${quando}${limpo ? `: ${limpo}` : '.'}`;
}
